import { useEffect, useState } from "react";
import type { ScheduleMonthDto } from "@madamuh/domain";
import { Link, useParams } from "react-router-dom";
import {
  fetchAdminScheduleMonth,
  generateAdminScheduleMonth,
  publishAdminScheduleMonth,
  validateAdminScheduleMonth
} from "../../shared/api/admin-schedule";
import { useSessionBootstrap } from "../../shared/session/SessionBootstrapProvider";
import { PageScaffold } from "../../shared/ui/PageScaffold";

const adminLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/events", label: "イベント管理" }
];

function formatDateTime(value: string | null): string {
  if (!value) {
    return "未実行";
  }

  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}

export function AdminScheduleMonthPage() {
  const { tenantSlug = "unknown-tenant", yyyymm = "" } = useParams();
  const { status, session, errorMessage, bootstrapSession } = useSessionBootstrap();
  const [scheduleMonth, setScheduleMonth] = useState<ScheduleMonthDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionState, setActionState] = useState<"idle" | "running">("idle");
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));

  useEffect(() => {
    if (status === "idle") {
      void bootstrapSession(tenantSlug);
    }
  }, [bootstrapSession, status, tenantSlug]);

  useEffect(() => {
    if (status !== "authenticated" || !hasAdminRole || !yyyymm) {
      return;
    }

    let cancelled = false;

    async function loadScheduleMonth() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextScheduleMonth = await fetchAdminScheduleMonth(tenantSlug, yyyymm);

        if (cancelled) {
          return;
        }

        setScheduleMonth(nextScheduleMonth);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        setLoadState("error");
        setLoadError(error instanceof Error ? error.message : "schedule month の取得に失敗しました");
      }
    }

    void loadScheduleMonth();

    return () => {
      cancelled = true;
    };
  }, [hasAdminRole, status, tenantSlug, yyyymm]);

  async function handleAction(action: "generate" | "validate" | "publish") {
    if (!yyyymm) {
      return;
    }

    setActionState("running");
    setLoadError(null);

    try {
      const nextScheduleMonth =
        action === "generate"
          ? await generateAdminScheduleMonth(tenantSlug, yyyymm)
          : action === "validate"
            ? await validateAdminScheduleMonth(tenantSlug, yyyymm)
            : await publishAdminScheduleMonth(tenantSlug, yyyymm, {
                expectedDraftRevision: scheduleMonth?.draftRevision ?? 0
              });

      setScheduleMonth(nextScheduleMonth);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "schedule month 操作に失敗しました");
    } finally {
      setActionState("idle");
    }
  }

  return (
    <PageScaffold
      title="Admin Schedule Month"
      description="月次 schedule の generate / validate / publish を行う最小画面です。"
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {status === "loading" ? <p>セッションを確認しています。</p> : null}
        {status === "unauthenticated" ? (
          <p>
            admin 画面には session が必要です。<Link to={`/t/${tenantSlug}/entry`}>/entry</Link> で mock operator login を実行してください。
          </p>
        ) : null}
        {status === "error" ? <p>セッション取得に失敗しました: {errorMessage}</p> : null}
        {status === "authenticated" && !hasAdminRole ? <p>この画面は admin role のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>schedule month を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {scheduleMonth ? (
          <>
            <section style={{ display: "grid", gap: 8 }}>
              <strong style={{ fontSize: 20 }}>{scheduleMonth.yearMonth}</strong>
              <div style={{ fontSize: 14, opacity: 0.8 }}>status: {scheduleMonth.status}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>draftRevision: {scheduleMonth.draftRevision}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>publishedRevision: {scheduleMonth.publishedRevision}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>hasUnpublishedChanges: {String(scheduleMonth.hasUnpublishedChanges)}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>lastGeneratedAt: {formatDateTime(scheduleMonth.lastGeneratedAt)}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>lastValidatedAt: {formatDateTime(scheduleMonth.lastValidatedAt)}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>lastPublishedAt: {formatDateTime(scheduleMonth.lastPublishedAt)}</div>
            </section>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button type="button" onClick={() => void handleAction("generate")} disabled={actionState === "running"}>
                {actionState === "running" ? "処理中..." : "Generate"}
              </button>
              <button type="button" onClick={() => void handleAction("validate")} disabled={actionState === "running"}>
                {actionState === "running" ? "処理中..." : "Validate"}
              </button>
              <button type="button" onClick={() => void handleAction("publish")} disabled={actionState === "running"}>
                {actionState === "running" ? "処理中..." : "Publish"}
              </button>
            </div>

            <section style={{ display: "grid", gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Validation</h2>
              <div style={{ fontSize: 14 }}>blocking: {scheduleMonth.blockingErrorCount}</div>
              <div style={{ fontSize: 14 }}>warning: {scheduleMonth.warningCount}</div>
              {scheduleMonth.validationIssues.length === 0 ? <p style={{ margin: 0 }}>issue はありません。</p> : null}
              {scheduleMonth.validationIssues.map((validationIssue) => (
                <article
                  key={`${validationIssue.issueCode}:${validationIssue.occurrenceId ?? "month"}`}
                  style={{ border: "1px solid #e5e5e5", borderRadius: 12, padding: 12, display: "grid", gap: 6 }}
                >
                  <strong>{validationIssue.severity}</strong>
                  <div>{validationIssue.message}</div>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>
                    occurrence: {validationIssue.occurrenceId ?? "month-level"}
                  </div>
                </article>
              ))}
            </section>

            <section style={{ display: "grid", gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Occurrences</h2>
              {scheduleMonth.occurrences.map((occurrence) => (
                <Link
                  key={occurrence.occurrenceId}
                  to={`/t/${tenantSlug}/teacher/admin/occurrences/${occurrence.occurrenceId}`}
                  style={{
                    border: "1px solid #e5e5e5",
                    borderRadius: 12,
                    padding: 12,
                    textDecoration: "none",
                    color: "inherit",
                    display: "grid",
                    gap: 6
                  }}
                >
                  <strong>{occurrence.lessonTitle}</strong>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>
                    revision {occurrence.revision} / {occurrence.syncState} / {occurrence.resolutionType}
                  </div>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>{occurrence.startAt.slice(0, 16)} - {occurrence.endAt.slice(0, 16)}</div>
                  {occurrence.publicNote ? <div style={{ fontSize: 14 }}>{occurrence.publicNote}</div> : null}
                </Link>
              ))}
            </section>
          </>
        ) : null}
      </div>
    </PageScaffold>
  );
}
